import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { billsAPI, BASE_URL } from '../services/api';
import { Bill, BillItem, SplitResult } from '../types';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { SplitwiseSyncModal } from '../components/features/SplitwiseSyncModal';
import { splitwiseAPI } from '../services/api';

const BillDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [bill, setBill] = useState<Bill | null>(null);
  const [items, setItems] = useState<BillItem[]>([]);
  const [splitResult, setSplitResult] = useState<SplitResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [splitwiseConnected, setSplitwiseConnected] = useState(false);
  const [billSynced, setBillSynced] = useState(false);

  useEffect(() => {
    // Check Splitwise connection status
    splitwiseAPI.getStatus()
      .then((data) => setSplitwiseConnected(data.connected))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Check if bill is already synced
    if (id) {
      splitwiseAPI.getSyncStatus(id)
        .then((data) => setBillSynced(data.synced))
        .catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadBillData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll for OCR status when processing
  useEffect(() => {
    if (!bill || bill.ocr_status === 'completed' || bill.ocr_status === 'failed') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const data = await billsAPI.getById(id!);
        setBill(data.bill);
        setItems(data.items || []);

        // Stop polling when done
        if (data.bill.ocr_status === 'completed' || data.bill.ocr_status === 'failed') {
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill?.ocr_status, id]);

  const loadBillData = async () => {
    try {
      const data = await billsAPI.getById(id!);
      setBill(data.bill);
      setItems(data.items || []);
    } catch (error) {
      console.error('Failed to load bill:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateSplit = async () => {
    setCalculating(true);
    try {
      const result = await billsAPI.calculateSplit(id!);
      setSplitResult(result);
    } catch (error) {
      console.error('Failed to calculate split:', error);
      alert('Failed to calculate split');
    } finally {
      setCalculating(false);
    }
  };

  const handleUpdateCategory = async (itemId: string, category: string) => {
    try {
      await billsAPI.updateItem(id!, itemId, { category });
      loadBillData();
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        title="Bill Details"
        showBack
        rightAction={
          bill?.image_url && (
            <button
              onClick={() => setShowImage(true)}
              className="p-2 rounded-xl hover:bg-slate-100"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          )
        }
      />

      <main className="p-4 pb-8 space-y-6">
        {/* Bill Summary Card */}
        <Card>
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-slate-900">
              {bill?.restaurant_name || 'Unknown Restaurant'}
            </h1>
            <p className="text-sm text-slate-500">
              {bill?.bill_date
                ? new Date(bill.bill_date).toLocaleDateString('en-IN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'Date not available'}
            </p>
          </div>

          <div className="flex justify-center items-baseline gap-1 mb-6">
            <span className="text-3xl font-bold text-slate-900">
              ₹{Number(bill?.total || 0).toLocaleString('en-IN')}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <p className="text-slate-500">Subtotal</p>
              <p className="font-semibold">₹{Number(bill?.subtotal || 0).toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-slate-500">Tax</p>
              <p className="font-semibold">₹{Number(bill?.tax || 0).toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-slate-500">Tip</p>
              <p className="font-semibold">₹{Number(bill?.tip || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* OCR Status */}
          {bill?.ocr_status !== 'completed' && (
            <div className="mt-4 p-3 bg-amber-50 rounded-xl text-center">
              <Badge variant="warning">
                {bill?.ocr_status === 'processing' ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Scanning bill...
                  </span>
                ) : (
                  `OCR: ${bill?.ocr_status}`
                )}
              </Badge>
            </div>
          )}
        </Card>

        {/* Items Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Items ({items.length})</h2>
          </div>

          {items.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <p className="text-slate-500">
                  {bill?.ocr_status === 'processing'
                    ? 'Scanning bill items...'
                    : 'No items found'}
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <Card key={item.id} padding="sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{item.name}</p>
                      <p className="text-sm text-slate-500">
                        {item.quantity}x @ ₹{Number(item.unit_price).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        ₹{Number(item.total_price).toLocaleString('en-IN')}
                      </p>
                      <select
                        value={item.category || 'shared'}
                        onChange={(e) => handleUpdateCategory(item.id, e.target.value)}
                        className="mt-1 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20"
                      >
                        <option value="vegetarian">Veg</option>
                        <option value="non_vegetarian">Non-veg</option>
                        <option value="alcohol">Alcohol</option>
                        <option value="beverage">Beverage</option>
                        <option value="shared">Shared</option>
                      </select>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Calculate Split Button */}
        {items.length > 0 && !splitResult && (
          <Button
            onClick={handleCalculateSplit}
            loading={calculating}
            fullWidth
            size="lg"
          >
            Calculate Split
          </Button>
        )}

        {/* Split Results */}
        {splitResult && (
          <section className="space-y-4">
            <h2 className="font-semibold text-slate-900">Split Results</h2>

            <div className="space-y-3">
              {splitResult.splits.map((split) => (
                <Card key={split.userId}>
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar name={split.userName} size="md" />
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{split.userName}</p>
                    </div>
                    <p className="text-xl font-bold text-green-600">
                      ₹{Number(split.totalAmount).toLocaleString('en-IN')}
                    </p>
                  </div>

                  <div className="text-sm space-y-1 text-slate-600 border-t border-slate-100 pt-3">
                    <div className="flex justify-between">
                      <span>Items</span>
                      <span>₹{Number(split.itemsTotal).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax share</span>
                      <span>₹{Number(split.taxShare).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tip share</span>
                      <span>₹{Number(split.tipShare).toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {split.itemBreakdown.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-sm text-green-600 cursor-pointer font-medium">
                        View item breakdown
                      </summary>
                      <div className="mt-2 text-xs space-y-1 text-slate-500">
                        {split.itemBreakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="truncate mr-2">{item.itemName}</span>
                            <span>₹{Number(item.assignedAmount).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </Card>
              ))}
            </div>

            {/* Suggested Payments */}
            {splitResult.optimizedPayments.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-slate-900 mb-3">Suggested Payments</h3>
                <Card className="bg-green-50 border-green-100">
                  <div className="space-y-3">
                    {splitResult.optimizedPayments.map((payment, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <Avatar name={payment.fromUserName} size="sm" />
                        <div className="flex-1 flex items-center gap-2">
                          <span className="font-medium text-slate-900">{payment.fromUserName}</span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                          <span className="font-medium text-slate-900">{payment.toUserName}</span>
                        </div>
                        <span className="font-bold text-green-700">
                          ₹{Number(payment.amount).toLocaleString('en-IN')}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* Sync to Splitwise */}
            {splitwiseConnected && !billSynced && (
              <Button
                onClick={() => setShowSyncModal(true)}
                fullWidth
                className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-500/25"
              >
                Sync to Splitwise
              </Button>
            )}
            {billSynced && (
              <div className="p-3 bg-green-50 rounded-xl text-center">
                <p className="text-sm text-green-700 font-medium">Already synced to Splitwise</p>
              </div>
            )}

            {/* Recalculate */}
            <Button
              onClick={handleCalculateSplit}
              variant="secondary"
              fullWidth
            >
              Recalculate Split
            </Button>
          </section>
        )}
      </main>

      {/* Splitwise Sync Modal */}
      {splitResult && bill && (
        <SplitwiseSyncModal
          isOpen={showSyncModal}
          onClose={() => {
            setShowSyncModal(false);
            // Re-check sync status after closing
            if (id) {
              splitwiseAPI.getSyncStatus(id)
                .then((data) => setBillSynced(data.synced))
                .catch(() => {});
            }
          }}
          billId={id!}
          billTotal={Number(bill.total)}
          splitResult={splitResult}
        />
      )}

      {/* Image Modal */}
      {showImage && bill?.image_url && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowImage(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white"
            onClick={() => setShowImage(false)}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={`${BASE_URL}/${bill.image_url}`}
            alt="Bill"
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default BillDetails;
